import { createFileRoute } from '@tanstack/react-router';
import { Grid, Group, Stack, Text } from '@mantine/core';
import { Clock, Monitor, Network, ShieldOff } from 'lucide-react';
import { Suspense, lazy, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  PageContainer,
  PageHeader,
  Pill,
  Section,
  Skeleton,
  StatusDot,
} from '../components/ui';
import { useNow } from '../hooks/useNow';
import { useRange } from '../hooks/useRanges';
import { useRangeHealth } from '../hooks/useRangeHealth';
import { useAuthStore } from '../store/useAuthStore';
import { RangeConsoleView } from '../components/range/RangeConsoleView';
import type { RangeHealthVm } from '../api/types';

const VMTopology = lazy(() => import('../components/range/VMTopology'));

export const Route = createFileRoute('/trainee/$rangeId')({
  component: TraineeRangeView,
});

/**
 * Trainee range view — focused single-page layout deliberately different
 * from the instructor's /ranges/$id (no destroy controls, no phase timeline,
 * no detection events, no admin actions; just what the trainee needs).
 *
 * Layout:
 *   1. Header: name, status, time-remaining
 *   2. Topology + My Workstations (grid)
 *      - Topology shows ALL VMs in the range (lab understanding)
 *      - Workstations are filtered to VMs assigned to the caller
 *   3. "Open console" on a workstation reveals the embedded RangeConsoleView
 *      below the grid (operator design 2026-05-27, revised): the Guacamole
 *      iframe now embeds IN-PAGE rather than the prior window.open new-tab
 *      default. A "Pop out" button inside the console preserves the detach-to-
 *      new-tab affordance (reusing the same minted token — no token change).
 */
export function TraineeRangeView() {
  const { rangeId } = Route.useParams();
  const role = useAuthStore((s) => s.user?.role);
  const ownSub = useAuthStore((s) => s.user?.userId) ?? '';
  const rangeQ = useRange(rangeId);
  const healthQ = useRangeHealth(rangeId);
  const [showConsolePane, setShowConsolePane] = useState(false);

  // Both branches sit INSIDE PageContainer — hand-rolled loading/error states
  // used to render flush against the viewport edge with no page chrome.
  if (rangeQ.isPending || healthQ.isPending) {
    return (
      <PageContainer size="wide">
        <Stack gap="lg">
          <Skeleton height={48} width="40%" />
          <Skeleton height={280} radius="md" />
        </Stack>
      </PageContainer>
    );
  }
  if (rangeQ.isError || !rangeQ.data) {
    return (
      <PageContainer size="wide">
        <ErrorState
          title="Range not available"
          error="This range was not found, or it is not assigned to you."
          onRetry={() => void rangeQ.refetch()}
        />
      </PageContainer>
    );
  }

  const range = rangeQ.data;
  const allVms = healthQ.data?.vms ?? [];
  const isTrainee = role === 'trainee';
  const myVms = isTrainee
    ? allVms.filter((v) => v.assignedTo && v.assignedTo === ownSub)
    : allVms;

  return (
    <PageContainer size="wide">
      <RangeHeader range={range} />

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Card>
            <Stack gap="sm">
              <Group gap="xs">
                <Network size={16} color="var(--mantine-color-dimmed)" />
                <Text fw={600}>
                  Topology
                </Text>
                <Pill variant="info">{allVms.length} VMs</Pill>
              </Group>
              {allVms.length > 0 ? (
                <Suspense fallback={<Skeleton lines={4} height={64} gap={6} />}>
                  <VMTopology vms={range.vms ?? []} />
                </Suspense>
              ) : (
                <Text c="dimmed" size="sm">
                  Topology will render once the range is fully provisioned.
                </Text>
              )}
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Card>
            <Stack gap="sm">
              <Group gap="xs">
                <Monitor size={16} color="var(--mantine-color-dimmed)" />
                <Text fw={600}>
                  My workstations
                </Text>
                <Pill variant="info">{myVms.length}</Pill>
              </Group>
              {myVms.length === 0 ? (
                <NoVmsAssigned isTrainee={isTrainee} />
              ) : (
                <Stack gap="sm">
                  {myVms.map((vm) => (
                    <VmCard
                      key={vm.instanceId}
                      vm={vm}
                      onOpenConsole={() => setShowConsolePane(true)}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {showConsolePane && myVms.length > 0 && (
        <Section title="Console" className="mt-2">
          <Card>
            <RangeConsoleView rangeId={rangeId} assignedVms={myVms} />
          </Card>
        </Section>
      )}
    </PageContainer>
  );
}

function RangeHeader({
  range,
}: {
  range: {
    scenarioName: string;
    status: string;
    expiresAt: string | null;
    currentPhase: string | null;
    objectivesCompleted: number;
    objectivesTotal: number;
  };
}) {
  const expires = useExpiresLabel(range.expiresAt);
  return (
    <PageHeader
      icon={Monitor}
      title={range.scenarioName}
      breadcrumbs={[
        { label: 'Forge', to: '/' },
        { label: 'My Ranges', to: '/trainee' },
        { label: 'Detail' },
      ]}
      actions={
        <Group gap="xs">
          <Pill variant={range.status === 'ready' ? 'success' : 'info'}>{range.status}</Pill>
          {range.currentPhase ? <Badge variant="info">Phase: {range.currentPhase}</Badge> : null}
          <Badge>
            {range.objectivesCompleted}/{range.objectivesTotal} objectives
          </Badge>
          {expires ? (
            <Group gap={4} c="dimmed">
              <Clock size={12} />
              <Text size="xs">{expires}</Text>
            </Group>
          ) : null}
        </Group>
      }
    />
  );
}

function VmCard({
  vm,
  onOpenConsole,
}: {
  vm: RangeHealthVm;
  onOpenConsole: () => void;
}) {
  const ready = vm.healthy;
  const proto = (vm.consoleProtocol || 'ssh').toUpperCase();
  return (
    // Bordered row, not a nested <Card> — a card inside a card double-draws the
    // surface and reads as a UI bug.
    <div
      style={{
        border: '1px solid var(--c-bd)',
        borderRadius: 10,
        padding: 12,
        background: 'rgb(var(--c-panel))',
      }}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={4} style={{ flex: 1 }}>
          <Group gap="xs">
            <Text fw={600} ff="JetBrains Mono" size="sm">
              {vm.vmName || vm.hostname}
            </Text>
            <Pill variant={ready ? 'success' : 'info'}>
              {ready ? 'Ready' : vm.state || 'Provisioning'}
            </Pill>
            <Badge>{proto}</Badge>
          </Group>
          <Group gap="sm" c="dimmed">
            <Text size="xs">role: {vm.role}</Text>
            {vm.privateIp ? <Text size="xs">{vm.privateIp}</Text> : null}
            <Group gap={4}>
              <StatusDot
                state={vm.ssmStatus === 'Online' ? 'running' : 'warning'}
              />
              <Text size="xs">SSM {vm.ssmStatus}</Text>
            </Group>
          </Group>
        </Stack>
        <Button
          variant="primary"
          size="sm"
          disabled={!ready}
          onClick={onOpenConsole}
        >
          Open console
        </Button>
      </Group>
    </div>
  );
}

function NoVmsAssigned({ isTrainee }: { isTrainee: boolean }) {
  return (
    <Stack gap="sm" align="center" py="lg">
      <ShieldOff size={28} color="var(--mantine-color-dimmed)" />
      <Text fw={600} size="sm">
        No workstations assigned to you
      </Text>
      <Text c="dimmed" size="xs" ta="center" maw={320}>
        {isTrainee
          ? 'Your instructor has not assigned you a workstation in this range yet.'
          : 'No VMs in this range are tagged with assignments. Switch to a trainee account to test the filter.'}
      </Text>
    </Stack>
  );
}

/** Live "time left" label — ticks via useNow instead of freezing at mount. */
function useExpiresLabel(iso: string | null): string | null {
  const now = useNow();
  if (!iso) return null;
  try {
    const remaining = new Date(iso).getTime() - now;
    if (remaining <= 0) return 'expired';
    const mins = Math.floor(remaining / 60_000);
    if (mins < 60) return `${mins}m left`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem === 0 ? `${hrs}h left` : `${hrs}h ${rem}m left`;
  } catch {
    return null;
  }
}
